import { supabase } from "../supabaseClient";

/** 10 Mo — aligné sur storage.buckets.file_size_limit */
export const DEVIS_SIGNE_MAX_BYTES = 10 * 1024 * 1024;

export const DEVIS_SIGNE_MAX_MB = 10;

export const DEVIS_SIGNE_ACCEPT =
  "application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp";

export const DEVIS_SIGNE_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

export type DevisSigneMime = (typeof DEVIS_SIGNE_MIME_TYPES)[number];

const EXT_TO_MIME: Record<string, DevisSigneMime> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
};

export const DEVIS_SIGNE_SIZE_ERROR = `⚠️ Le fichier est trop lourd. Limite : ${DEVIS_SIGNE_MAX_MB} Mo maximum.`;

export const DEVIS_SIGNE_TYPE_ERROR =
  "⚠️ Format non accepté. Utilisez un PDF ou une image (JPEG, PNG, WebP).";

function resolveMime(file: File): DevisSigneMime | null {
  const t = file.type?.toLowerCase();
  if (DEVIS_SIGNE_MIME_TYPES.includes(t as DevisSigneMime)) return t as DevisSigneMime;
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext && ext in EXT_TO_MIME) return EXT_TO_MIME[ext];
  return null;
}

export type DevisSigneValidationResult =
  | { ok: true; mime: DevisSigneMime }
  | { ok: false; message: string };

export function validateDevisSigneFile(file: File): DevisSigneValidationResult {
  const mime = resolveMime(file);
  if (!mime) return { ok: false, message: DEVIS_SIGNE_TYPE_ERROR };
  if (file.size > DEVIS_SIGNE_MAX_BYTES) return { ok: false, message: DEVIS_SIGNE_SIZE_ERROR };
  if (file.size === 0) return { ok: false, message: "⚠️ Le fichier est vide." };
  return { ok: true, mime };
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Impossible de lire l'image"));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Compression impossible"))),
      type,
      quality
    );
  });
}

/**
 * Réduit les photos lourdes via Canvas (sans lib externe).
 * PDF inchangé. Si la compression échoue ou dépasse encore 10 Mo, on garde l'original
 * (la validation bloquera si nécessaire).
 */
export async function prepareDevisSigneFile(file: File): Promise<File> {
  const validation = validateDevisSigneFile(file);
  if (!validation.ok) throw new Error(validation.message);
  if (validation.mime === "application/pdf") return file;
  if (file.size <= 1.5 * 1024 * 1024) return file;

  try {
    const img = await loadImageFromFile(file);
    const maxDim = 2048;
    let { width, height } = img;
    if (width > maxDim || height > maxDim) {
      const ratio = Math.min(maxDim / width, maxDim / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const outputType =
      validation.mime === "image/png" ? "image/png" : validation.mime === "image/webp" ? "image/webp" : "image/jpeg";
    const quality = outputType === "image/png" ? undefined : 0.82;
    const blob = await canvasToBlob(canvas, outputType, quality);

    if (blob.size >= file.size) return file;

    const baseName = file.name.replace(/\.[^.]+$/, "") || "devis";
    const ext =
      outputType === "image/png" ? "png" : outputType === "image/webp" ? "webp" : "jpg";
    const compressed = new File([blob], `${baseName}.${ext}`, { type: outputType, lastModified: Date.now() });

    const recheck = validateDevisSigneFile(compressed);
    if (!recheck.ok) return file;
    return compressed;
  } catch {
    return file;
  }
}

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 120) || "devis";
}

export async function uploadDevisSigne(jobId: string, file: File): Promise<{ path: string; error: string | null }> {
  const prepared = await prepareDevisSigneFile(file);
  const validation = validateDevisSigneFile(prepared);
  if (!validation.ok) return { path: "", error: validation.message };

  const path = `${jobId}/${Date.now()}_${sanitizeFileName(prepared.name)}`;
  const { error } = await supabase.storage.from("devis_signes").upload(path, prepared, {
    cacheControl: "3600",
    upsert: true,
    contentType: validation.mime,
  });

  if (error) {
    const msg = error.message?.toLowerCase() ?? "";
    if (msg.includes("size") || msg.includes("payload") || msg.includes("large")) {
      return { path: "", error: DEVIS_SIGNE_SIZE_ERROR };
    }
    if (msg.includes("mime") || msg.includes("type")) {
      return { path: "", error: DEVIS_SIGNE_TYPE_ERROR };
    }
    return { path: "", error: error.message || "Échec de l'envoi du fichier." };
  }

  return { path, error: null };
}

export function isDevisSignePdfPath(storagePath: string): boolean {
  return storagePath.toLowerCase().endsWith(".pdf");
}

export async function getDevisSigneSignedUrl(storagePath: string, expiresIn = 3600): Promise<string | null> {
  const { data, error } = await supabase.storage.from("devis_signes").createSignedUrl(storagePath, expiresIn);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

export async function deleteDevisSigne(storagePath: string): Promise<{ error: string | null }> {
  const { error } = await supabase.storage.from("devis_signes").remove([storagePath]);
  if (error) return { error: error.message || "Impossible de supprimer le fichier." };
  return { error: null };
}
